import { MyListing } from '@/hotdesk-ui/services/listing.service';
import { FC, Fragment, useEffect, useMemo, useState } from 'react';
import Carousel from 'react-multi-carousel';
import 'react-multi-carousel/lib/styles.css';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useAppSelector } from '@/hotdesk-ui/hooks/hook';
import { RootState } from '@/hotdesk-ui/store/store';
import { currencyConvert } from '@/hotdesk-ui/helpers/utils';
import { buildWorkspacePath } from '@/hotdesk-ui/helpers/workspace-url';
import { NumberFormatService } from '@/hotdesk-ui/services/number-format.service';
import CardOnMap from '../card-on-map/CardOnMap';
import TruncatedText from '@/hotdesk-ui/components/shared/TruncatedText';
import styles from './ListingSliderPopup.module.scss';
export interface ListingSliderPopupProps {
  listings: MyListing[];
  type: string;
  languageCode?: string;
  cardType?: 'workspace' | 'listing';
  /** Renders the close (X) control on each slide's card when provided. */
  onClose?: () => void;
  prevClick?: (slider: Carousel | null) => void;
  nextClick?: (slider: Carousel | null) => void;
}
const responsive = {
  desktop: {
    breakpoint: {
      max: 3000,
      min: 0
    },
    items: 1,
    slidesToSlide: 1
  }
};
const getPriceUnit = (keys: string[]): string => {
  if (keys.includes('hour')) return 'hour';
  if (keys.includes('day')) return 'day';
  if (keys.includes('month')) return 'month';
  return 'annual';
};
const SlideCard: FC<{
  item: MyListing;
  languageCode: string;
  cardType: 'workspace' | 'listing';
  onClose?: () => void;
}> = ({
  item,
  languageCode,
  cardType,
  onClose
}) => {
  const defaultCurrency = useAppSelector((s: RootState) => s.currency.defaultCurrency);
  const currencyList = useAppSelector((s: RootState) => s.currency.currency);
  const {
    price,
    priceUnit,
    baseCurrencyName,
    url
  } = useMemo(() => {
    const unit = getPriceUnit(Object.keys(item.rules?.price || {}));
    const raw = item.rules?.price?.[unit];
    let formatted = '';
    if (defaultCurrency?.code && raw) {
      const converted = currencyConvert(currencyList, item.rules?.currency as string, defaultCurrency.code, raw);
      if (converted) {
        formatted = new NumberFormatService().formatNumberWithCountingLabel(converted, languageCode);
      }
    }
    const builtUrl = `/${buildWorkspacePath({
      country: item?.city?.country,
      city: item?.city,
      workspaceSlug: item?.workspace?.slug,
      listingSlug: cardType === 'workspace' ? undefined : item?.slug
    })}`;
    return {
      price: formatted,
      priceUnit: unit,
      baseCurrencyName: defaultCurrency?.name || '',
      url: builtUrl
    };
  }, [item, currencyList, defaultCurrency, languageCode, cardType]);
  return <CardOnMap item={item} languageCode={languageCode} baseCurrencyName={baseCurrencyName} price={price} price_unit={priceUnit} url={url} type={cardType} onClose={onClose} data-sentry-element="CardOnMap" data-sentry-component="SlideCard" data-sentry-source-file="ListingSliderPopup.tsx" />;
};
const ListingSliderPopup: FC<ListingSliderPopupProps> = ({
  listings = [],
  languageCode = '',
  cardType = 'listing',
  onClose
}) => {
  const [isRTL, setIsRTL] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [slider, setSlider] = useState<Carousel | null>(null);
  useEffect(() => {
    setIsRTL(typeof document !== 'undefined' && document.dir === 'rtl');
  }, [languageCode]);
  if (listings.length === 0) return null;
  const total = listings.length;
  const isFirst = currentSlide === 0;
  const isLast = currentSlide >= total - 1;
  return <Fragment>
            <div className={styles.popupSlider}>
                {total > 1 && <div className={styles.header}>
                        <span className={styles.headerTitle}>
                            <span className={styles.headerPrefix}>
                                <span className={styles.headerCount}>
                                    {currentSlide + 1}
                                </span>{' '}
                                of{' '}
                                <span className={styles.headerCount}>
                                    {total}
                                </span>{' '}
                                {cardType === 'workspace' ? 'workspaces' : <>
                                        Listings{' '}
                                        <span className={styles.headerMuted}>
                                            inside
                                        </span>
                                    </>}
                            </span>
                            {cardType !== 'workspace' && listings[currentSlide]?.workspace?.title && <TruncatedText text={listings[currentSlide]?.workspace?.title as string} className={styles.headerWorkspace} />}
                        </span>
                        <div className={styles.headerNav}>
                            <button type="button" className={styles.navBtn} onClick={e => {
            e.stopPropagation();
            slider?.goToSlide(currentSlide - 1);
          }} disabled={isFirst} aria-label="Previous">
                                <ChevronLeft size={16} />
                            </button>
                            <button type="button" className={styles.navBtn} onClick={e => {
            e.stopPropagation();
            slider?.goToSlide(currentSlide + 1);
          }} disabled={isLast} aria-label="Next">
                                <ChevronRight size={16} />
                            </button>
                        </div>
                    </div>}

                <Carousel ssr={true} rtl={isRTL} responsive={responsive} arrows={false} ref={el => setSlider(el)} deviceType="desktop" infinite={false} draggable={false} swipeable={false} beforeChange={nextSlide => setCurrentSlide(nextSlide)} data-sentry-element="Carousel" data-sentry-source-file="ListingSliderPopup.tsx">
                    {listings.map((listing, idx) => <SlideCard key={`popup-slide-${listing.id ?? idx}`} item={listing} languageCode={languageCode} cardType={cardType} onClose={onClose} />)}
                </Carousel>
            </div>
        </Fragment>;
};
export default ListingSliderPopup;